import React, {useState, useEffect, useCallback, useMemo} from "react";
import {
    Table, Space, Button, message, Card, Typography,
    Modal, Divider, Tabs, Tag, Tooltip,
} from "antd";
import {CheckOutlined, EyeOutlined, ClockCircleOutlined} from "@ant-design/icons";
import {approvalBaseColumns} from './constants';
import apiClient from './apiHelper';
import {ENDPOINTS, REQUEST_TYPES, MESSAGES} from './apiConfig';
import {ApprovalFilterBar} from './FilterBar';

const {Title} = Typography;

const Approvals = ({userId}) => {
    const [activeTab, setActiveTab] = useState("pending");
    const [pendingTasks, setPendingTasks] = useState([]);
    const [approvedTasks, setApprovedTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const [userFilterPending, setUserFilterPending] = useState([]);
    const [projectFilterPending, setProjectFilterPending] = useState([]);
    const [userFilterApproved, setUserFilterApproved] = useState([]);
    const [projectFilterApproved, setProjectFilterApproved] = useState([]);

    const fetchPendingApprovals = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiClient(ENDPOINTS.TASK.FETCH_PENDING_APPROVALS, {
                method: 'POST',
                body: JSON.stringify({request_type: REQUEST_TYPES.FETCH_PENDING_APPROVALS})
            });
            const sorted = [...res].sort((a, b) => new Date(a.date) - new Date(b.date));
            setPendingTasks(sorted);
        } catch (error) {
            message.error(MESSAGES.ERROR.FETCH_PENDING_APPROVALS);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchApprovedTasks = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiClient(ENDPOINTS.TASK.FETCH_APPROVED_TASKS, {
                method: 'POST',
                body: JSON.stringify({request_type: REQUEST_TYPES.FETCH_APPROVED_TASKS})
            });
            const sorted = [...res].sort((a, b) => new Date(a.date) - new Date(b.date));
            setApprovedTasks(sorted);
        } catch (error) {
            message.error(MESSAGES.ERROR.FETCH_APPROVED_TASKS);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPendingApprovals();
        fetchApprovedTasks();
    }, [fetchPendingApprovals, fetchApprovedTasks]);

    const handleTabChange = (key) => {
        setActiveTab(key);
        if (key === "pending") fetchPendingApprovals();
        else if (key === "approved") fetchApprovedTasks();
    };

    const handleRevert = async (taskId) => {
        try {
            const res = await apiClient(ENDPOINTS.TASK.REVERT_TASK, {
                method: 'POST',
                body: JSON.stringify({request_type: REQUEST_TYPES.REVERT_TASK, task_id: taskId})
            });
            if (res.success) {
                message.success(MESSAGES.SUCCESS.REVERT_TASK);
                const revertedTask = approvedTasks.find((t) => t.task_id === taskId);
                setApprovedTasks((prev) => prev.filter((t) => t.task_id !== taskId));
                if (revertedTask) {
                    setPendingTasks((prev) => [{...revertedTask, status: 'pending'}, ...prev]);
                }
            }
        } catch (error) {
            message.error(MESSAGES.ERROR.REVERT_TASK);
        }
    };

    const handleApprove = async (taskId = null) => {
        const isBulk = !taskId;
        const ids = isBulk ? selectedRowKeys : [taskId];

        if (isBulk && ids.length === 0) return message.warning("No tasks selected");
        if (isBulk) setLoading(true);

        try {
            const data = await apiClient(ENDPOINTS.TASK.APPROVE_TASK, {
                method: 'POST',
                body: JSON.stringify({
                    action: "approve",
                    task_ids: ids,
                    request_type: REQUEST_TYPES.APPROVE_TASK
                })
            });

            const approvedIds = data.approved || [];
            const failedItems = data.failed || [];

            const approvedBatch = pendingTasks
                .filter((t) => approvedIds.includes(t.task_id))
                .map(t => ({...t, status: 'approved'}));

            setPendingTasks((prev) => prev.filter((t) => !approvedIds.includes(t.task_id)));
            setApprovedTasks((prev) => [...approvedBatch, ...prev]);

            if (isBulk) setSelectedRowKeys([]);

            if (failedItems.length === 0) {
                message.success(
                    isBulk
                        ? `${approvedBatch.length} tasks approved successfully`
                        : MESSAGES.SUCCESS.APPROVE_TASK
                );
            } else {
                message.warning(`${approvedBatch.length} approved, ${failedItems.length} failed`);
            }
        } catch (error) {
            message.error(isBulk ? "Failed to approve selected tasks" : MESSAGES.ERROR.APPROVE_TASK);
        } finally {
            if (isBulk) setLoading(false);
        }
    };

    const renderProjectTags = (project) => {
        const projectArray = Array.isArray(project) ? project : (project ? project.split(',').map(p => p.trim()) : []);
        if (projectArray.length === 0) return <span style={{color: '#bfbfbf'}}>N/A</span>;
        return (
            <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px'}}>
                {projectArray.map((p) => (
                    <Tooltip title={p} key={p} mouseEnterDelay={0.5}>
                        <Tag color="gray" style={{
                            margin: 0, fontSize: '11px', maxWidth: '100px',
                            display: 'inline-block', overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom'
                        }}>
                            {p}
                        </Tag>
                    </Tooltip>
                ))}
            </div>
        );
    };

    const pendingColumns = [
        ...approvalBaseColumns,
        {title: "Project", dataIndex: "project", key: "project", width: 150, render: renderProjectTags},
        {
            title: "Type", dataIndex: "type", key: "type", width: 130,
            ellipsis: {showTitle: false},
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>
        },
        {
            title: "Task", dataIndex: "task", key: "task", width: 150,
            ellipsis: {showTitle: false},
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>
        },
        {
            title: "Actions", key: "actions", width: 185,
            render: (_, record) => (
                <Space className="space-x-2">
                    <Button type="link" className="black-btn" icon={<EyeOutlined/>}
                            onClick={() => {setSelectedTask(record); setModalVisible(true);}}
                            size="small">View</Button>
                    <Button type="primary" className="black-btn" icon={<CheckOutlined/>}
                            onClick={() => handleApprove(record.task_id)}
                            size="small">Approve</Button>
                </Space>
            ),
        },
    ];

    const approvedColumns = [
        ...approvalBaseColumns,
        {title: "Project", dataIndex: "project", key: "project", width: 150, render: renderProjectTags},
        {
            title: "Type", dataIndex: "type", key: "type", width: 90,
            ellipsis: {showTitle: false},
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>
        },
        {
            title: "Task", dataIndex: "task", key: "task", width: 90,
            ellipsis: {showTitle: false},
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>
        },
        {title: "Status", key: "status", width: 100, render: () => <Tag color="green">APPROVED</Tag>},
        {
            title: "Actions", key: "actions", width: 220,
            render: (_, record) => (
                <Space className="space-x-2">
                    <Button type="link" className="black-btn" icon={<EyeOutlined/>}
                            onClick={() => {setSelectedTask(record); setModalVisible(true);}}
                            size="small" danger>View</Button>
                    <Button type="primary" className="black-btn" icon={<ClockCircleOutlined/>}
                            size="small" onClick={() => handleRevert(record.task_id)}>Mark Pending</Button>
                </Space>
            ),
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys)
    };

    const getUniqueOptions = (tasks, key) => {
        const uniqueValues = new Set();
        tasks.forEach((t) => {
            const value = t[key];
            if (Array.isArray(value)) value.forEach((v) => {if (v) uniqueValues.add(v);});
            else if (value) uniqueValues.add(value);
        });
        return Array.from(uniqueValues).sort().map((val) => ({label: val, value: val}));
    };

    const pendingUserOptions = useMemo(() => getUniqueOptions(pendingTasks, "user_id"), [pendingTasks]);
    const pendingProjectOptions = useMemo(() => getUniqueOptions(pendingTasks, "project"), [pendingTasks]);
    const approvedUserOptions = useMemo(() => getUniqueOptions(approvedTasks, "user_id"), [approvedTasks]);
    const approvedProjectOptions = useMemo(() => getUniqueOptions(approvedTasks, "project"), [approvedTasks]);

    const filteredPending = useMemo(() => pendingTasks.filter((t) => {
        const userOk = userFilterPending.length ? userFilterPending.includes(t.user_id) : true;
        const projectOk = projectFilterPending.length
            ? (Array.isArray(t.project) ? t.project.some(p => projectFilterPending.includes(p)) : projectFilterPending.includes(t.project))
            : true;
        return userOk && projectOk;
    }), [pendingTasks, userFilterPending, projectFilterPending]);

    const filteredApproved = useMemo(() => approvedTasks.filter((t) => {
        const userOk = userFilterApproved.length ? userFilterApproved.includes(t.user_id) : true;
        const projectOk = projectFilterApproved.length
            ? (Array.isArray(t.project) ? t.project.some(p => projectFilterApproved.includes(p)) : projectFilterApproved.includes(t.project))
            : true;
        return userOk && projectOk;
    }), [approvedTasks, userFilterApproved, projectFilterApproved]);

    const pendingCount = filteredPending.length;
    const approvedCount = filteredApproved.length;

    const tabItems = [
        {
            key: "pending",
            label: <span>Pending Approvals <Tag color="blue" style={{marginLeft: 6}}>{pendingCount}</Tag></span>,
            children: (
                <>
                    <ApprovalFilterBar
                        userOptions={pendingUserOptions}
                        projectOptions={pendingProjectOptions}
                        userValue={userFilterPending}
                        projectValue={projectFilterPending}
                        onUserChange={setUserFilterPending}
                        onProjectChange={setProjectFilterPending}
                        onRefresh={fetchPendingApprovals}
                        loading={loading && activeTab === "pending"}
                        extraActions={
                            <Button type="primary" onClick={() => handleApprove()}
                                    disabled={selectedRowKeys.length === 0} danger>
                                Approve Selected ({selectedRowKeys.length})
                            </Button>
                        }
                    />
                    <Table rowSelection={rowSelection} columns={pendingColumns} dataSource={filteredPending}
                           rowKey="task_id" loading={loading && activeTab === "pending"}
                           pagination={{pageSize: 10}} tableLayout="fixed"
                           locale={{emptyText: "No pending approvals"}}/>
                </>
            ),
        },
        {
            key: "approved",
            label: <span>Approved Tasks <Tag color="green" style={{marginLeft: 6}}>{approvedCount}</Tag></span>,
            children: (
                <>
                    <ApprovalFilterBar
                        userOptions={approvedUserOptions}
                        projectOptions={approvedProjectOptions}
                        userValue={userFilterApproved}
                        projectValue={projectFilterApproved}
                        onUserChange={setUserFilterApproved}
                        onProjectChange={setProjectFilterApproved}
                        onRefresh={fetchApprovedTasks}
                        loading={loading && activeTab === "approved"}
                    />
                    <Table columns={approvedColumns} dataSource={filteredApproved} rowKey="task_id"
                           loading={loading && activeTab === "approved"} pagination={{pageSize: 10}}
                           tableLayout="fixed" locale={{emptyText: "No approved tasks"}}/>
                </>
            ),
        },
    ];

    return (
        <div>
            <Card title={<Title level={4} style={{marginBottom: 0}}>Approvals</Title>}>
                <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems}/>
            </Card>

            <Modal
                title={<Title level={4} style={{fontWeight: "bold"}}>Task Details</Title>}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={[
                    <Button key="back" onClick={() => setModalVisible(false)}>Close</Button>,
                    activeTab === "pending" && (
                        <Button key="approve" type="primary" icon={<CheckOutlined/>} danger
                                onClick={() => {handleApprove(selectedTask?.task_id); setModalVisible(false);}}>
                            Approve
                        </Button>
                    ),
                ].filter(Boolean)}
                width={800}
            >
                <Divider style={{marginTop: 0}}/>
                {selectedTask && (
                    <div className="space-y-4">
                        <div><strong>User ID:</strong> {selectedTask.user_id}</div>
                        <div><strong>Date:</strong> {selectedTask.date}</div>
                        <div><strong>Day:</strong> {selectedTask.day}</div>
                        <div>
                            <strong style={{display: 'flex', marginBottom: '8px'}}>Projects:</strong>
                            {renderProjectTags(selectedTask.project)}
                        </div>
                        <div><strong>Task:</strong> {selectedTask.task}</div>
                        <div>
                            <strong>Summary:</strong>
                            <p className="whitespace-pre-wrap bg-gray-50 p-3 rounded">{selectedTask.summary}</p>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default Approvals;